import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowRight,
  ChevronDown,
  ChevronRight,
  CheckCircle2,
  Quote,
  FileText,
  FileSpreadsheet,
  BarChart3,
  ShieldCheck,
  RefreshCw,
  Layers,
  PlugZap,
  Table2,
  GitCompareArrows,
  Landmark,
  Globe2,
  Calculator,
} from 'lucide-react';
import Header from '@/components/layout/Header';
import Footer from '@/components/layout/Footer';
import { publishedSaftCountries } from '@/data/saftCountries';
/* ------------------------------------------------------------------ */
/*  Copy lives here, so it is easy to change in one place               */
/* ------------------------------------------------------------------ */
const HERO = {
  headlineStart: 'Global indirect tax',
  headlineAccent: 'compliance',
  tagline: 'Automating compliance around the world.',
};
const TEAMS = [
  {
    icon: Landmark,
    team: 'Tax',
    text: 'Every mandate in every country, in one place. You file with confidence, without spreadsheets.',
  },
  {
    icon: BarChart3,
    team: 'Finance',
    text: 'Returns, ledgers and reports that agree with each other, so the close does not stall.',
  },
  {
    icon: PlugZap,
    team: 'IT',
    text: 'Runs inside your ERP. Nothing new to build, host or maintain.',
  },
];
const WHY_NOW = [
  {
    icon: Globe2,
    title: 'More mandates, in more countries',
    text: 'e-Invoicing, SAF-T and real-time reporting are arriving country by country. Each one has its own format, deadline and portal.',
  },
  {
    icon: GitCompareArrows,
    title: 'Authorities now compare your data',
    text: 'Tax authorities increasingly cross-check your e-invoices, audit files and VAT returns against each other. A mismatch is visible to them.',
  },
  {
    icon: Table2,
    title: 'Separate tools, numbers that disagree',
    text: 'One tool per mandate and spreadsheets in between produce figures that do not match. That is exactly what authorities look for.',
  },
];
const PRINCIPLES = [
  { icon: PlugZap, title: 'Real data from your ERP', text: 'Taxera sits inside SAP and other ERPs and works on live transactions, not exports.' },
  { icon: Table2, title: 'No Excel in the middle', text: 'Nobody re-keys or patches numbers on the way to the tax authority.' },
  { icon: RefreshCw, title: 'Reconciled by design', text: 'e-Invoices, SAF-T and returns come from the same data, so they agree before the authority checks.' },
];
const SOLUTIONS = [
  { icon: FileText, title: 'e-Invoicing', text: 'Structured, validated and delivered in real time, in every mandated country.', href: '/solutions/e-invoicing' },
  { icon: FileSpreadsheet, title: 'SAF-T', text: 'Audit files generated, checked and submitted from your ERP, country by country.', href: '/solutions/saf-t' },
  { icon: BarChart3, title: 'VAT returns, EC Sales List, Intrastat', text: 'Periodic returns built from the same live data as your invoices.', href: '/products?hub=tax-reporting-hub' },
  { icon: ShieldCheck, title: 'eCompliance', text: 'A tax control framework with drill-down from the KPI to the document.', href: '/products?hub=ecompliance-hub' },
  { icon: RefreshCw, title: 'Reconciliations', text: 'Invoices, SAF-T and returns cross-checked before the authority does it.', href: '/products?hub=tax-operations-hub' },
  { icon: Layers, title: 'One platform', text: 'All of it on one platform with one ERP integration, workflows and alerts included.', href: '/platform' },
];
const TESTIMONIALS = [
  { company: 'Leading Global FMCG Company', role: 'Head of Tax Technology, Europe', quote: 'We went from managing 14 separate e-Invoicing tools across EMEA to one platform. The middleware approach meant zero disruption to our SAP landscape.', useCase: 'e-Invoicing' },
  { company: 'Global Discount Retailer', role: 'VP Finance & Tax', quote: 'The volume of B2C invoices we process daily made compliance a nightmare. Taxera handles it at scale — across every country we operate in — without us thinking about it.', useCase: 'eReporting' },
  { company: 'Global B2B Travel Management Company', role: 'Global Tax Director', quote: 'Multi-country digital services VAT was our biggest headache. The automated country-mandate updates alone justified the investment within the first quarter.', useCase: 'Tax Operations' },
  { company: 'Global Travel Technology Provider', role: 'CFO EMEA', quote: 'What convinced us was the middleware integration. It sits inside our ERP, not outside it. No batch uploads, no file transfers — real-time compliance.', useCase: 'Technology' },
  { company: 'European Steel & Manufacturing Group', role: 'Head of SAP Competence Center', quote: 'Our S/4HANA migration was complex enough without worrying about tax compliance. Taxera gave us a single integration point for SAF-T, e-Invoicing, and statutory reporting across 8 countries.', useCase: 'SAP S/4HANA' },
];
const FAQ_ITEMS = [
  { q: 'What is Taxera?', a: 'Taxera is an enterprise-grade cloud platform that helps multinational companies manage global indirect tax compliance, including e-Invoicing, SAF-T, VAT returns, and tax reconciliation.' },
  { q: 'Which ERP systems does Taxera integrate with?', a: 'Taxera integrates natively with SAP, Oracle, and Microsoft Dynamics, as well as other standard and non-standard ERP systems.' },
  { q: 'Is Taxera compliant with GDPR and data privacy regulations?', a: 'Yes. Taxera is ISO 27001 certified, GDPR compliant, and gives clients full flexibility over their data processing location and hyperscaler.' },
  { q: 'Which countries does Taxera cover?', a: 'Taxera covers tax compliance obligations across a growing number of countries and jurisdictions worldwide, and is continuously updated as new regulations are introduced globally.' },
  { q: 'What is e-Invoicing and why does it matter?', a: 'e-Invoicing is the digital exchange of invoices directly between companies and tax authorities. It is becoming mandatory across the EU and globally under regulations like ViDA.' },
  { q: 'How long does implementation take?', a: 'Implementation timelines vary by complexity, but Taxera is designed for rapid deployment with pre-built ERP connectors and a dedicated onboarding team.' },
  { q: 'Who is Taxera built for?', a: 'Taxera is built for tax directors, CFOs, and finance teams at multinational corporations who need a single, reliable hub for all indirect tax obligations.' },
];
/* ------------------------------------------------------------------ */
const FAQAccordion = ({ question, answer, index }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-slate-200 rounded-xl overflow-hidden bg-white" data-testid={`faq-item-${index}`}>
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-slate-50 transition-colors"
        data-testid={`faq-toggle-${index}`}
        aria-expanded={open}
      >
        <span className="text-sm font-semibold text-taxera-charcoal pr-4">{question}</span>
        <ChevronDown className={`w-4 h-4 text-slate-400 shrink-0 transition-transform duration-200 ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && <p className="px-5 pb-4 text-sm text-slate-600 leading-relaxed">{answer}</p>}
    </div>
  );
};
const fadeUp = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
};
const HomePage = () => {
  const saftCountries = publishedSaftCountries();
  return (
    <div className="min-h-screen bg-white" data-testid="home-page">
      <Header />
      <main>
        {/* 1. Hero: what we do, in one sentence */}
        <section className="relative overflow-hidden pt-32 md:pt-40 pb-16 md:pb-24 min-h-[88vh] flex items-center" data-testid="home-hero">
          <div className="absolute inset-0 bg-gradient-to-b from-slate-50 via-white to-white" />
          <div className="absolute top-0 right-0 w-1/2 h-2/3 bg-gradient-to-bl from-taxera-mint/10 to-transparent" />
          <div className="relative z-10 max-w-5xl mx-auto px-6 text-center w-full">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
              <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-taxera-charcoal leading-[1.1]" style={{ textWrap: 'balance' }}>
                {HERO.headlineStart} <span className="taxera-gradient-text">{HERO.headlineAccent}</span>
              </h1>
              <p className="mt-5 text-2xl md:text-3xl font-semibold text-slate-600 max-w-3xl mx-auto">{HERO.tagline}</p>
              <div className="mt-9 flex flex-col sm:flex-row items-center justify-center gap-3">
                <Link
                  to="/contact"
                  className="group inline-flex items-center gap-2 px-8 py-4 taxera-gradient text-white rounded-full font-semibold text-base hover:shadow-2xl transition-all hover:scale-105"
                  data-testid="hero-book-demo"
                >
                  Book a demo <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </Link>
                <Link
                  to="/calculator"
                  className="inline-flex items-center gap-2 px-8 py-4 border border-slate-300 hover:border-taxera-blue text-taxera-charcoal rounded-full font-semibold text-base transition-colors"
                  data-testid="hero-calculator"
                >
                  What is compliance really costing you?
                </Link>
              </div>
              <div className="mt-10 flex flex-wrap items-center justify-center gap-x-6 gap-y-2" data-testid="hero-proof">
                {['Works inside SAP and other ERPs', 'Live in many countries', 'Reconciled by design'].map((item) => (
                  <span key={item} className="flex items-center gap-1.5 text-sm text-slate-500 font-medium">
                    <CheckCircle2 className="w-4 h-4 text-taxera-teal shrink-0" /> {item}
                  </span>
                ))}
              </div>
            </motion.div>
          </div>
        </section>
        {/* 2. Who it is for: tax, finance and IT */}
        <section className="py-14 lg:py-20 border-y border-slate-100 bg-slate-50/60" data-testid="for-teams">
          <div className="max-w-6xl mx-auto px-6">
            <motion.div {...fadeUp} className="text-center mb-10 max-w-3xl mx-auto">
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-taxera-charcoal" style={{ textWrap: 'balance' }}>
                Making compliance easier for tax, finance and IT.
              </h2>
              <p className="mt-4 text-base md:text-lg text-slate-600 leading-relaxed">
                We integrate with your ERP and work alongside your team, from the first call to go-live.
              </p>
            </motion.div>
            <div className="grid md:grid-cols-3 gap-5">
              {TEAMS.map((item, i) => (
                <motion.div key={item.team} {...fadeUp} transition={{ delay: i * 0.08 }} className="rounded-2xl border border-slate-200 bg-white p-7" data-testid={`team-${item.team.toLowerCase()}`}>
                  <div className="w-11 h-11 rounded-xl bg-taxera-blue/[0.07] flex items-center justify-center mb-4">
                    <item.icon className="w-5 h-5 text-taxera-blue" strokeWidth={1.5} />
                  </div>
                  <h3 className="font-heading text-lg font-semibold text-taxera-charcoal mb-2">{item.team}</h3>
                  <p className="text-sm text-slate-600 leading-relaxed">{item.text}</p>
                </motion.div>
              ))}
            </div>
            <div className="mt-8 flex flex-wrap items-center justify-center gap-x-6 gap-y-2" data-testid="trust-credentials-bar">
              {['ISO 27001', 'Swiss-built', 'SAP BTP Partner', 'Peppol Certified', 'GENA Member'].map((item) => (
                <span key={item} className="flex items-center gap-1.5 text-xs text-slate-500 font-medium tracking-wide">
                  <CheckCircle2 className="w-3 h-3 text-taxera-teal shrink-0" /> {item}
                </span>
              ))}
            </div>
          </div>
        </section>
        {/* 3. Why now */}
        <section className="py-16 lg:py-24" data-testid="why-now">
          <div className="max-w-6xl mx-auto px-6">
            <motion.div {...fadeUp} className="text-center mb-12 max-w-3xl mx-auto">
              <p className="text-xs uppercase tracking-[0.25em] font-semibold text-taxera-teal mb-3">Why it matters now</p>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-taxera-charcoal">
                Tax authorities are comparing everything you send them.
              </h2>
            </motion.div>
            <div className="grid md:grid-cols-3 gap-5">
              {WHY_NOW.map((item, i) => (
                <motion.div key={item.title} {...fadeUp} transition={{ delay: i * 0.08 }} className="rounded-2xl border border-slate-200 bg-white p-7" data-testid={`why-now-${i + 1}`}>
                  <div className="w-11 h-11 rounded-xl bg-taxera-blue/[0.07] flex items-center justify-center mb-4">
                    <item.icon className="w-5 h-5 text-taxera-blue" strokeWidth={1.5} />
                  </div>
                  <h3 className="font-heading text-lg font-semibold text-taxera-charcoal mb-2">{item.title}</h3>
                  <p className="text-sm text-slate-600 leading-relaxed">{item.text}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
        {/* 4. How it works */}
        <section className="py-16 lg:py-24 bg-slate-50" data-testid="how-it-works">
          <div className="max-w-6xl mx-auto px-6">
            <motion.div {...fadeUp} className="text-center mb-12 max-w-3xl mx-auto">
              <p className="text-xs uppercase tracking-[0.25em] font-semibold text-taxera-teal mb-3">How it works</p>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-taxera-charcoal mb-3">
                From your ERP to the tax authority, in one flow.
              </h2>
              <p className="text-base text-slate-500">
                Taxera works for any industry, because it is built around your ERP, not your sector. If you run SAP or another ERP, it fits.
              </p>
            </motion.div>
            <motion.div {...fadeUp} className="flex flex-col md:flex-row items-stretch justify-center gap-4 md:gap-0 mb-12" data-testid="flow">
              {[
                { icon: Layers, label: 'Your ERP', detail: 'SAP, Oracle, Microsoft Dynamics and others', color: '#0066CC' },
                { icon: ShieldCheck, label: 'Taxera platform', detail: 'Extracts, validates, reconciles, submits', color: '#14A3B8' },
                { icon: Landmark, label: 'Tax authorities', detail: 'In every country you operate in', color: '#8b5cf6' },
              ].map((step, i, arr) => (
                <div key={step.label} className="flex flex-col md:flex-row items-center">
                  <div className="w-full md:w-60 rounded-2xl border-2 bg-white p-6 text-center shadow-sm" style={{ borderColor: step.color }}>
                    <step.icon className="w-6 h-6 mx-auto mb-3" style={{ color: step.color }} strokeWidth={1.5} />
                    <div className="font-heading text-lg font-bold text-taxera-charcoal">{step.label}</div>
                    <div className="text-xs text-slate-500 mt-1.5 leading-snug">{step.detail}</div>
                  </div>
                  {i < arr.length - 1 && <ChevronRight className="w-6 h-6 text-taxera-teal mx-3 my-2 md:my-0 rotate-90 md:rotate-0" />}
                </div>
              ))}
            </motion.div>
            <div className="grid md:grid-cols-3 gap-5">
              {PRINCIPLES.map((p, i) => (
                <motion.div key={p.title} {...fadeUp} transition={{ delay: i * 0.08 }} className="rounded-2xl bg-white border border-slate-200 p-6" data-testid={`principle-${i + 1}`}>
                  <p.icon className="w-5 h-5 text-taxera-teal mb-3" strokeWidth={1.5} />
                  <h3 className="font-heading font-semibold text-taxera-charcoal mb-1.5">{p.title}</h3>
                  <p className="text-sm text-slate-600 leading-relaxed">{p.text}</p>
                </motion.div>
              ))}
            </div>
            <div className="text-center mt-8">
              <Link to="/platform" className="inline-flex items-center gap-2 text-sm font-semibold text-taxera-blue hover:text-taxera-charcoal transition-colors group" data-testid="how-platform-link">
                See the platform in detail <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
              </Link>
            </div>
          </div>
        </section>
        {/* 5. What Taxera covers */}
        <section className="py-16 lg:py-24" data-testid="solutions-grid">
          <div className="max-w-6xl mx-auto px-6">
            <motion.div {...fadeUp} className="text-center mb-12 max-w-3xl mx-auto">
              <p className="text-xs uppercase tracking-[0.25em] font-semibold text-taxera-teal mb-3">Solutions</p>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-taxera-charcoal">Everything your tax team files, on one platform.</h2>
            </motion.div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {SOLUTIONS.map((s, i) => (
                <motion.div key={s.title} {...fadeUp} transition={{ delay: i * 0.06 }}>
                  <Link
                    to={s.href}
                    className="group block h-full rounded-2xl border border-slate-200 bg-white p-6 hover:border-taxera-teal/40 hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300"
                    data-testid={`solution-${s.title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                  >
                    <div className="w-10 h-10 rounded-xl bg-taxera-blue/[0.07] group-hover:bg-taxera-teal/[0.1] flex items-center justify-center mb-4 transition-colors">
                      <s.icon className="w-5 h-5 text-taxera-blue group-hover:text-taxera-teal transition-colors" strokeWidth={1.5} />
                    </div>
                    <h3 className="font-heading text-base font-semibold text-taxera-charcoal mb-1.5">{s.title}</h3>
                    <p className="text-sm text-slate-500 leading-relaxed mb-3">{s.text}</p>
                    <span className="inline-flex items-center gap-1 text-xs font-semibold text-taxera-teal">
                      Learn more <ArrowRight className="w-3 h-3 group-hover:translate-x-0.5 transition-transform" />
                    </span>
                  </Link>
                </motion.div>
              ))}
            </div>
            {saftCountries.length > 0 && (
              <div className="mt-8 flex flex-wrap items-center justify-center gap-3" data-testid="home-saft-countries">
                <span className="text-sm text-slate-500">SAF-T guides by country:</span>
                {saftCountries.map((c) => (
                  <Link key={c.slug} to={`/solutions/saf-t/${c.slug}`} className="text-sm font-semibold px-4 py-1.5 rounded-full border border-slate-200 hover:border-taxera-blue hover:text-taxera-blue transition-colors" data-testid={`home-saft-${c.slug}`}>
                    {c.name}
                  </Link>
                ))}
              </div>
            )}
          </div>
        </section>
        {/* 6. SAP */}
        <section className="py-16 lg:py-20 bg-slate-900" data-testid="home-sap">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <p className="text-xs uppercase tracking-[0.25em] font-semibold text-taxera-teal mb-3">For SAP customers</p>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-4">Moving to S/4HANA? Tax is the forgotten workstream.</h2>
            <p className="text-base text-slate-400 max-w-2xl mx-auto mb-8">
              Tax set-ups built in ECC do not carry over by themselves. Taxera runs on SAP BTP and fits into your migration, so compliance does not become the thing that delays it.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link to="/sap-s4hana" className="group inline-flex items-center gap-2 px-7 py-3.5 taxera-gradient text-white rounded-full font-semibold hover:shadow-xl transition-all" data-testid="home-sap-cta">
                SAP S/4HANA and tax <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link to="/sap-s4hana/assessment" className="inline-flex items-center gap-2 px-7 py-3.5 border border-white/20 text-white rounded-full font-semibold hover:bg-white/10 transition-colors" data-testid="home-sap-assessment">
                Check your readiness
              </Link>
            </div>
          </div>
        </section>
        {/* 7. Client voices */}
        <section id="testimonials" className="py-16 lg:py-24" data-testid="social-proof">
          <div className="max-w-6xl mx-auto px-6">
            <motion.div {...fadeUp} className="text-center mb-12">
              <p className="text-xs uppercase tracking-[0.25em] font-semibold text-taxera-teal mb-3">Client voices</p>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-taxera-charcoal">What our clients say</h2>
            </motion.div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {TESTIMONIALS.map((item, i) => (
                <motion.div
                  key={item.company}
                  {...fadeUp}
                  transition={{ delay: i * 0.08 }}
                  className={`rounded-2xl border border-slate-200 bg-white p-6 flex flex-col ${i === 4 ? 'md:col-span-2 lg:col-span-1' : ''}`}
                  data-testid={`testimonial-${item.company.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <Quote className="w-5 h-5 text-taxera-teal/40 mb-3 rotate-180" />
                  <p className="text-sm text-slate-600 leading-relaxed flex-1 mb-4">{item.quote}</p>
                  <div className="flex items-center justify-between pt-3 border-t border-slate-100">
                    <div>
                      <div className="text-sm font-semibold text-taxera-charcoal">{item.company}</div>
                      <div className="text-xs text-slate-400">{item.role}</div>
                    </div>
                    <span className="text-2xs font-semibold uppercase tracking-wider text-taxera-teal bg-taxera-teal/[0.06] px-2.5 py-1 rounded-full">{item.useCase}</span>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
        {/* 8. Tracker + calculator */}
        <section className="py-16 lg:py-20 bg-slate-50" data-testid="home-tools">
          <div className="max-w-6xl mx-auto px-6 grid md:grid-cols-2 gap-6">
            <motion.div {...fadeUp} className="rounded-2xl bg-white border border-slate-200 p-8" data-testid="home-tracker">
              <Globe2 className="w-6 h-6 text-taxera-teal mb-4" strokeWidth={1.5} />
              <h3 className="font-heading text-xl font-bold text-taxera-charcoal mb-2">Global Tax Tracker</h3>
              <p className="text-sm text-slate-600 leading-relaxed mb-5">
                Free and always current: e-Invoicing, SAF-T and VAT mandates in 56 countries, with dates, scope and what to do about them.
              </p>
              <Link to="/tracker" className="group inline-flex items-center gap-2 text-sm font-semibold text-taxera-blue hover:text-taxera-charcoal transition-colors" data-testid="home-tracker-link">
                Explore the tracker <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
              </Link>
            </motion.div>
            <motion.div {...fadeUp} transition={{ delay: 0.08 }} className="rounded-2xl bg-white border border-slate-200 p-8" data-testid="home-calculator">
              <Calculator className="w-6 h-6 text-taxera-teal mb-4" strokeWidth={1.5} />
              <h3 className="font-heading text-xl font-bold text-taxera-charcoal mb-2">What is compliance really costing you?</h3>
              <p className="text-sm text-slate-600 leading-relaxed mb-5">
                Answer two questions and see an estimate of what your current set-up costs in vendors, systems and people.
              </p>
              <Link to="/calculator" className="group inline-flex items-center gap-2 text-sm font-semibold text-taxera-blue hover:text-taxera-charcoal transition-colors" data-testid="home-calculator-link">
                Find my number <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
              </Link>
            </motion.div>
          </div>
        </section>
        {/* 9. FAQ */}
        <section id="faq" className="py-16 lg:py-20" data-testid="faq-section">
          <div className="max-w-3xl mx-auto px-6">
            <motion.div {...fadeUp} className="text-center mb-10">
              <p className="text-xs uppercase tracking-[0.25em] font-semibold text-taxera-teal mb-3">Frequently asked questions</p>
              <h2 className="font-heading text-3xl font-bold text-taxera-charcoal">Common questions about Taxera</h2>
            </motion.div>
            <div className="space-y-3" data-testid="faq-list">
              {FAQ_ITEMS.map((faq, i) => (
                <FAQAccordion key={faq.q} question={faq.q} answer={faq.a} index={i} />
              ))}
            </div>
          </div>
        </section>
        {/* 10. Final CTA */}
        <section className="pb-16 lg:pb-24" data-testid="final-cta-section">
          <div className="max-w-3xl mx-auto px-6">
            <motion.div {...fadeUp} className="taxera-gradient rounded-3xl p-10 lg:p-14 text-center relative overflow-hidden">
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(255,255,255,0.15)_0%,transparent_50%)]" />
              <div className="relative z-10">
                <h2 className="font-heading text-2xl lg:text-3xl font-bold text-white mb-3">See it running on your own ERP data.</h2>
                <p className="text-base text-white/80 mb-8 max-w-md mx-auto">Book a short demo and we will show you the full path from your ERP to the tax authority.</p>
                <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                  <Link to="/contact" className="group inline-flex items-center gap-2 px-8 py-4 bg-white text-taxera-blue rounded-full font-semibold hover:shadow-2xl transition-all hover:scale-105" data-testid="final-cta-demo">
                    Book a demo <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </Link>
                  <Link to="/evaluate" className="inline-flex items-center gap-2 px-8 py-4 bg-white/20 text-white rounded-full font-semibold hover:bg-white/30 transition-all border border-white/30" data-testid="final-cta-evaluate">
                    Take the compliance diagnostic
                  </Link>
                </div>
              </div>
            </motion.div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};
export default HomePage;
